import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { getUserWatchHistory } from "../helpers/watchHistoryHelpers";
import { useAuth } from "./AuthContext";

const HistoryContext = createContext<{
  history: any[];
  historyLoading?: boolean;
  historyError?: string | null;
  fetchHistory: () => Promise<void>;
}>({
  history: [],
  historyLoading: false,
  historyError: null,
  fetchHistory: async () => {},
});

export const HistoryProvider = ({ children }: { children: ReactNode }) => {
    const [history, setHistory] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [historyError, setHistoryError] = useState<string | null>(null);

    const { authUser } = useAuth();
    const userId = authUser ? authUser.uid : "";

    async function fetchHistory() {
        setHistoryLoading(true);
        try {
            const data = await getUserWatchHistory(userId);
            if (!Array.isArray(data)) {
                throw new Error("Invalid data format received from server.");
            }

            // Fetch TMDB details for each item
            const apiKey = process.env.REACT_APP_TMDB_API_KEY;
            const detailedResults = await Promise.all(
                data.map(async (item: any) => {
                    try {
                        const response = await fetch(
                            `https://api.themoviedb.org/3/${item.content_type}/${item.tmdb_id}?language=en-US`,
                            {
                            headers: {
                                Accept: "application/json",
                                Authorization: `Bearer ${apiKey}`,
                            },
                            }
                        );
                        const tmdb = await response.json();
                        return {
                            ...item,
                            id: item.tmdb_id,
                            title: tmdb.title || tmdb.name || item.title,
                            poster_path: tmdb.poster_path,
                            release_date: tmdb.release_date || tmdb.first_air_date || null,
                            character: tmdb.character || null,
                            media_type: item.content_type,
                        };
                    } catch (err) {
                        console.warn(`Failed to fetch TMDB data for ${item.id}`, err);
                        return item;
                    }
                })
            );

            setHistory(detailedResults);
        } catch (err: any) {
            setHistoryError(err.message);
        } finally {
            setHistoryLoading(false);
        }
    }

    useEffect(() => {
        if (!userId) {
            setHistoryLoading(false);
            setHistory([]);
            return;
        }
        
        fetchHistory();
    }, [userId]);

    return (
        <HistoryContext.Provider value={{ history, historyLoading, historyError, fetchHistory }}>
            {children}
        </HistoryContext.Provider>
    );
};

export const useHistory = () => useContext(HistoryContext);